import { computed, DestroyRef, effect, inject, Injectable, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  defaultTreeOptions,
  MAX_ROWS_COUNT_ERROR,
  MAX_ROWS_COUNT_WARNING,
  TreeListAction,
  TreeListColumn,
} from '@app/orders/models/dx-tree-list.model';
import { TreeListRowData } from '@app/orders/models/orders.model';
import { OrdersActionsService } from '@app/orders/services/orders-actions.service';
import { OrdersCellRenderingService } from '@app/orders/services/orders-cell-rendering.service';
import { OrdersConfirmDialogService } from '@app/orders/services/orders-confirm-dialog.service';
import { OrdersSelectionService } from '@app/orders/services/orders-selection.service';
import { OrdersStatusService } from '@app/orders/services/orders-status.service';
import { OrdersTreeDatasourceService } from '@app/orders/services/orders-tree-datasource.service';
import { OrdersTreeListStore } from '@app/orders/services/orders-tree-list.store';
import { OrdersTreeListUtilsService } from '@app/orders/services/orders-tree-utils.service';
import { OrdersService } from '@app/orders/services/orders.service';
import { TreeListUtilsService } from '@app/orders/services/tree-list-utils.service';
import {
  CustomerIdentificationEnumResponseDto,
  OrderByDirectionEnum,
  OrderByPropertyOfOrderItemPropertyOrderByEnumRequestDto,
  OrderItemPropertyOrderByEnumRequestDto,
  OrderItemRowTypeEnumResponseDto,
  OrdersCustomerTypeEnumResponseDto,
} from '@core/services/apiclient';
import { NotificationHubService } from '@core/services/notifications/NotificationHub.service';
import { select, Store } from '@ngrx/store';
import * as fromOrders from '@root-store/orders-store/index';
import { OffcanvasComponent } from '@shared/components';
import { TableFilterResult } from '@shared/models/table-filter.model';
import CustomStore from 'devextreme/data/custom_store';
import dxTreeList, { CellPreparedEvent, RowDblClickEvent, SelectionChangedEvent } from 'devextreme/ui/tree_list';
import { filter, Observable } from 'rxjs';

@Injectable()
export class OrdersTreeListFacade {
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject<Store<fromOrders.State>>(Store);
  private readonly ordersUtils = inject(OrdersTreeListUtilsService);
  private readonly treeListUtils = inject(TreeListUtilsService);
  private readonly ordersService = inject(OrdersService);
  private readonly datasourceService = inject(OrdersTreeDatasourceService);
  private readonly selectionService = inject(OrdersSelectionService);
  private readonly cellRenderingService = inject(OrdersCellRenderingService);
  private readonly dialogService = inject(OrdersConfirmDialogService);
  private readonly actionsService = inject(OrdersActionsService);
  private readonly statusService = inject(OrdersStatusService);
  private readonly notificationHubService = inject(NotificationHubService);
  private readonly state = inject(OrdersTreeListStore);

  readonly orderItemRowType = OrderItemRowTypeEnumResponseDto;
  readonly customerTypeEnumResponseDto = OrdersCustomerTypeEnumResponseDto;
  readonly identificationType = CustomerIdentificationEnumResponseDto;

  readonly currentDataLength = computed(() => {
    if (this.datasourceService.isPaginatedMode()) {
      return this.datasourceService.totalCount() ?? 0;
    }
    return this.datasourceService.currentData()?.length ?? 0;
  });

  readonly actualPageSize = computed(() => {
    const size = this.state.pageSize();
    return size === 0 ? this.currentDataLength() : size;
  });

  readonly totalPages = computed(() => {
    const total = this.currentDataLength() ?? 0;
    const size = this.actualPageSize();
    if (!this.datasourceService.isPaginatedMode()) return 1;
    return size > 0 ? Math.max(1, Math.ceil(total / size)) : 1;
  });

  readonly typesEffect = effect(() => {
    this.ordersUtils.itemRowTypes();
    this.applyTypesAndFilters({ resetPagination: false });
  });

  readonly eticketEffect = effect(() => {
    const eticket = this.notificationHubService.eTicketNotifSignal();
    if (eticket?.length) {
      setTimeout(() => {
        this.deselectAllRows();
        this.reloadTreeData();
      }, 1000);
    }
  });

  readonly selectedRows = this.selectionService.selectedRows;
  readonly selectedTotalAmount = this.selectionService.selectedTotalAmount;
  readonly selectedSeatsCount = this.selectionService.selectedSeatsCount;
  readonly selectedFoldersCount = this.selectionService.selectedFoldersCount;
  readonly selectedOrdersCount = this.selectionService.selectedOrdersCount;
  readonly isSelectAllValidationPending = this.selectionService.isSelectAllValidationPending;
  readonly selectedFoldersIds = this.selectionService.selectedFoldersIds;

  readonly currentPage = this.state.currentPage;
  readonly pageSize = this.state.pageSize;
  readonly categoryTooltip = this.state.categoryTooltip;
  readonly rateTooltip = this.state.rateTooltip;

  treeOptions = {
    ...defaultTreeOptions,
    remoteOperations: { ...defaultTreeOptions.remoteOperations },
    pagination: { ...defaultTreeOptions.pagination },
  };
  dataSource: CustomStore<TreeListRowData, number>;
  columns: TreeListColumn[];
  actions: TreeListAction[] = [];
  fixedActions: TreeListAction[] = [];
  onContentReadyCallback: (event: any) => void;

  private tooltipCache = new Map<string, string>();
  private loadingTooltips = new Set<string>();
  private treeInstanceGetter: (() => dxTreeList | undefined) | null = null;

  init(): void {
    this.dataSource = this.datasourceService.createStore();
    this.columns = this.ordersUtils.getTreeListColumns();
    this.prepareContentReadyCallback();
    this.pageSize.set(this.treeOptions.pagination.pageSize);
    this.datasourceService.setPagination({ current: this.currentPage(), size: this.actualPageSize() });

    this.store
      .pipe(select(fromOrders.selectors.selectOrderItemsFilters), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (filters) => {
          this.state.lastFilters.set((filters ?? []) as TableFilterResult[]);
          this.store.dispatch(
            fromOrders.actions.setIsDefaultOrdersList({
              isDefaultOrdersList: !(filters ?? []).length,
            }),
          );
          this.applyTypesAndFilters({ resetPagination: true });
        },
      });
  }

  initActions(offcanvas: OffcanvasComponent): void {
    this.actions = this.actionsService.getOrdersActions(offcanvas);
    this.fixedActions = this.actionsService.getOrdersFixedActions(offcanvas);
    if (this.actions) {
      this.listenToRowsHover();
    }
  }

  setTreeInstanceGetter(getter: () => dxTreeList | undefined): void {
    this.treeInstanceGetter = getter;
  }

  listenToPrintRequestCompleted(): void {
    this.store
      .pipe(
        select(fromOrders.selectors.selectPrintRequestCompleted),
        filter(Boolean),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (printRequestCompleted) => {
          if (printRequestCompleted) {
            this.deselectAllRows();
            this.store.dispatch(fromOrders.actions.resetPrintRequestCompleted());
          }
        },
      });
  }

  private applyTypesAndFilters(options?: { resetPagination?: boolean }): void {
    const filters = this.state.lastFilters() ?? [];
    const types = this.ordersUtils.itemRowTypes() ?? [];

    this.datasourceService.setTypesAndFilters({
      filters,
      types,
    });

    if (options?.resetPagination && !this.state.isPageSizeChanging()) {
      const hasFilters = !!filters?.length;
      if (!hasFilters) {
        this.currentPage.set(1);
        this.pageSize.set(0);
      } else if (this.pageSize() === 0) {
        this.pageSize.set(this.treeOptions.pagination.pageSize);
      }
      this.datasourceService.setPagination({ current: this.currentPage(), size: this.actualPageSize() });
    }
    this.deselectAllRows();
    this.reloadTreeData();
  }

  private listenToRowsHover(): void {
    this.treeListUtils.getRowHoverEvents().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  onPageChanged(page: number, treeInstance?: dxTreeList): void {
    this.state.isPageChanging.set(true);
    this.currentPage.set(page);
    this.datasourceService.setPagination({ current: this.currentPage(), size: this.actualPageSize() });
    this.reloadTreePreservingSelection(treeInstance ?? this.getTreeInstance());
    this.state.isPageChanging.set(false);
  }

  onPageSizeChanged(size: number, treeInstance?: dxTreeList): void {
    this.state.isPageSizeChanging.set(true);

    const hasActiveSelection = this.hasActiveSelection();
    if (size === 0) {
      const total = this.currentDataLength();
      if (total > MAX_ROWS_COUNT_ERROR) {
        this.dialogService.showPageSizeError(total);
        setTimeout(() => {
          this.pageSize.set(this.state.lastValidPageSize());
          this.state.isPageSizeChanging.set(false);
          if (hasActiveSelection) {
            this.selectionService.setIsRestoringSelection(false);
          }
        }, 100);
        return;
      }

      if (total > MAX_ROWS_COUNT_WARNING) {
        const onValidate = () => {
          this.pageSize.set(size);
          this.state.lastValidPageSize.set(total);
          this.datasourceService.setPagination({ current: this.currentPage(), size: total });
          this.reloadTreePreservingSelection(treeInstance ?? this.getTreeInstance());
          setTimeout(() => {
            this.state.isPageSizeChanging.set(false);
            if (hasActiveSelection) {
              this.selectionService.setIsRestoringSelection(false);
            }
          }, 100);
        };

        const onCancel = () => {
          this.state.isPageSizeChanging.set(false);
          if (hasActiveSelection) {
            this.selectionService.setIsRestoringSelection(false);
          }
          this.pageSize.set(this.state.lastValidPageSize());
        };

        this.dialogService.showPageSizeWarning(total, onValidate, onCancel);
        return;
      }

      this.pageSize.set(size);
      this.state.lastValidPageSize.set(total);
      this.datasourceService.setPagination({ current: this.currentPage(), size: total });
    } else {
      this.pageSize.set(size);
      this.state.lastValidPageSize.set(size);
      this.datasourceService.setPagination({ current: this.currentPage(), size });
    }
    this.reloadTreePreservingSelection(treeInstance ?? this.getTreeInstance());
    this.state.isPageSizeChanging.set(false);
  }

  deselectAllRows(treeInstance?: dxTreeList): void {
    const tree = treeInstance ?? this.getTreeInstance();
    this.selectionService.deselectAllRows(tree);
     this.state.pendingSelectionRestore.set(null);
  }

  deselectAllRowsFromActionBar(treeInstance?: dxTreeList): void {
    const tree = treeInstance ?? this.getTreeInstance();
    if (!tree) return;
    if (this.totalPages() > 1 && this.hasActiveSelection()) {
      this.selectionService.deselectAllRows(tree);
      tree.deselectAll();
      tree.clearSelection();
      tree.refresh();
      this.state.pendingSelectionRestore.set(null);
    } else {
      this.deselectAllRows(tree);
    }
  }

  private prepareContentReadyCallback(): void {
    this.onContentReadyCallback = (e) => {
      this.selectionService.setSelectAllCheckbox();
      this.repaintSelectAllCheckbox();

      if (this.state.pendingSelectionRestore()?.length) {
        const tree = e.component as dxTreeList;
        const currentData = this.datasourceService.currentData();
        const currentIds = new Set(currentData.map((r) => r.id));
        const keysToRestore = this.state.pendingSelectionRestore().filter((id) => currentIds.has(id));
        this.selectionService.setIsRestoringSelection(true);
        if (keysToRestore.length) {
          tree.selectRows(keysToRestore, false);
          this.selectionService.syncSelectedParents(tree);
        }
        this.selectionService.setIsRestoringSelection(false);
        this.state.pendingSelectionRestore.set(null);
      }
    };
  }

  onSelectionChanged(event: SelectionChangedEvent, treeInstance?: dxTreeList): void {
    if (
      this.datasourceService.activeSelectionOperation() !== 'idle' ||
      this.selectionService.getIsRestoringSelection()
    ) {
      return;
    }

    const isSelectAll =
      (event?.currentSelectedRowKeys?.length === this.totalCount ||
        event?.currentDeselectedRowKeys?.length === this.totalCount) &&
      this.totalCount > 1 &&
      this.selectionService.isSelectAllValidationPending();

    if (isSelectAll) {
      return;
    }

    this.selectionService.updateSelectionFromEvent(event);
    this.selectionService.syncSelectedParents(treeInstance ?? this.getTreeInstance());
    this.repaintSelectAllCheckbox();
  }

  toggleRow(key: number, treeInstance?: dxTreeList): void {
    const tree = treeInstance ?? this.getTreeInstance();
    if (!tree) return;

    const expandedKeysSet = this.state.expandedKeys();
    const willExpand = !expandedKeysSet.has(key);

    tree[willExpand ? 'expandRow' : 'collapseRow'](key);
    this.setExpandedKey(key, willExpand);

    if (!willExpand) return;

    const selectedRows = this.selectionService.selectedRows();

    const childKeys = selectedRows.filter((r) => r.parentId === key).map((r) => r.id);
    if (childKeys.length > 0) {
      this.state.pendingSelectionRestore.set(selectedRows.map((r) => r.id));
    }
  }

  toggleOrderFolders(orderId: number, treeInstance?: dxTreeList): void {
    this.selectionService
      .toggleOrderFoldersByOrderNumber({
        orderId,
        treeInstance: treeInstance ?? this.getTreeInstance(),
        includes: this.ordersUtils.itemRowTypes(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  getStatusChipData(rowData: TreeListRowData) {
    return this.statusService.getStatusChipData(rowData);
  }

  isExpanded(key: number, treeInstance?: dxTreeList): boolean {
    const expandedKeysSet = this.state.expandedKeys();
    if (expandedKeysSet.has(key)) {
      return true;
    }
    const tree = treeInstance ?? this.getTreeInstance();
    if (tree) {
      const expandedRowKeys = tree.option('expandedRowKeys') as number[] | undefined;
      if (Array.isArray(expandedRowKeys)) {
        return expandedRowKeys.includes(key);
      }
    }
    return false;
  }

  isOrderSelected(orderId: number): boolean {
    return this.selectionService.isOrderSelected(orderId);
  }

  isColumnSorted(treeInstance: dxTreeList | undefined, column: any, sortOrder: 'asc' | 'desc'): boolean {
    const columnOptions = (treeInstance ?? this.getTreeInstance())?.columnOption(column?.index);
    return columnOptions?.sortIndex !== null && columnOptions?.sortOrder === sortOrder;
  }

  sortColumn(
    event: Event,
    treeInstance: dxTreeList | undefined,
    column: any,
    sortOrder: 'asc' | 'desc',
    emit: boolean,
  ): void {
    event?.stopPropagation();

    const tree = treeInstance ?? this.getTreeInstance();
    if (!tree) return;
    const columnOption = tree.columnOption(column?.index);
    const columnId = column?.dataField as string | undefined;
    if (!columnId) return;

    const isAlreadySortedWithSameOrder =
      columnOption?.sortIndex !== null && columnOption?.sortOrder === sortOrder;
    tree.clearSorting();

    if (isAlreadySortedWithSameOrder) {
      this.columns = this.columns.map((c) => ({ ...c, sortIndex: undefined, sortOrder: undefined }));
      if (emit) this.onOrderByChanged(null);
      return;
    }

    tree.columnOption(columnId, { sortIndex: 0, sortOrder });
    this.columns = this.columns.map((c) => ({
      ...c,
      sortIndex: c.id === columnId ? 0 : undefined,
      sortOrder: c.id === columnId ? sortOrder : undefined,
    }));

    if (emit) this.onOrderByChanged({ property: columnId, direction: sortOrder });
  }

  onCellPrepared(event: CellPreparedEvent, treeInstance?: dxTreeList): void {
    this.cellRenderingService.processCellPrepared(event, (value) =>
      this.handleSelectAllCheckboxChange(value, treeInstance ?? this.getTreeInstance()),
    );
  }

  private onOrderByChanged(orderBy: { property: string; direction: 'asc' | 'desc' } | null): void {
    const orderByValue: OrderByPropertyOfOrderItemPropertyOrderByEnumRequestDto | null = orderBy
      ? {
          property: orderBy.property as OrderItemPropertyOrderByEnumRequestDto,
          direction: orderBy.direction as OrderByDirectionEnum,
        }
      : null;
    this.datasourceService.setOrderBy(orderByValue);
    this.reloadTreePreservingSelection();
  }

  private handleSelectAllCheckboxChange(value: boolean | undefined, treeInstance?: dxTreeList): void {
    if (value === undefined) return;

    if (this.state.isPageSizeChanging() || this.state.isPageChanging()) {
      return;
    }

    if (!value) {
      this.deselectAllRows();
      return;
    }

    this.selectionService.handleSelectAllCheckboxChange(treeInstance, this.ordersUtils.itemRowTypes());
  }

  repaintSelectAllCheckbox(): void {
    this.selectionService.repaintSelectAllCheckbox(
      this.selectionService.selectedFoldersIds().length,
      this.datasourceService.totalCount(),
    );
  }

  onRowDblClick(rowData: RowDblClickEvent, offcanvas: OffcanvasComponent): void {
    const data = rowData?.data;
    if (data?.orderItemRowType === OrderItemRowTypeEnumResponseDto.SubscriptionGroup) {
      this.store.dispatch(
        fromOrders.actions.loadOrderItems({
          orderId: data.orderId,
          orderItemId: null,
          subscriptionOrderId: data.id,
        }),
      );
    } else if (data?.orderItemRowType === OrderItemRowTypeEnumResponseDto.Subscription) {
      this.store.dispatch(
        fromOrders.actions.loadOrderItems({
          orderId: data.orderId,
          orderItemId: data.id,
          subscriptionOrderId: data.subscriptionOrderId,
        }),
      );
    } else {
      this.store.dispatch(
        fromOrders.actions.loadOrderItems({
          orderId: data?.orderId,
          orderItemId: data?.id,
          subscriptionOrderId: null,
        }),
      );
    }
    offcanvas.open();
  }

  closeOrderDetails(): void {
    this.store.dispatch(fromOrders.actions.resetOrderDetails());
  }

  loadMultiCategories(rowData: TreeListRowData): void {
    this.loadTooltipData(rowData, 'category', this.categoryTooltip, (sub, order) =>
      this.ordersService.getOrderMultiCategories(sub, order),
    );
  }

  loadMultiRates(rowData: TreeListRowData): void {
    this.loadTooltipData(rowData, 'rate', this.rateTooltip, (sub, order) =>
      this.ordersService.getOrderMultiRates(sub, order),
    );
  }

  private loadTooltipData(
    rowData: TreeListRowData,
    type: 'category' | 'rate',
    tooltipSignal: WritableSignal<string>,
    apiFn: (subscriptionOrderId: number | null, orderRowId: number | null) => Observable<string[]>,
  ): void {
    const field = rowData?.[type];
    if (!field?.hasMultiple) {
      tooltipSignal.set(field?.value ?? '');
      return;
    }

    const cacheKey = `${type}-${this.getCacheKey(rowData)}`;
    if (this.tooltipCache.has(cacheKey)) {
      tooltipSignal.set(this.tooltipCache.get(cacheKey));
      return;
    }

    if (this.loadingTooltips.has(cacheKey)) return;
    this.loadingTooltips.add(cacheKey);

    const { subscriptionOrderId, orderRowId } = this.getMultiValueApiParams(rowData);
    apiFn(subscriptionOrderId, orderRowId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (values: string[]) => {
          const value = values?.join(', ') ?? '';
          this.tooltipCache.set(cacheKey, value);
          tooltipSignal.set(value);
          this.loadingTooltips.delete(cacheKey);
        },
        error: () => this.loadingTooltips.delete(cacheKey),
      });
  }

  private getCacheKey(rowData: TreeListRowData): string {
    const { subscriptionOrderId, orderRowId } = this.getMultiValueApiParams(rowData);
    return `${rowData.orderId}-${subscriptionOrderId ?? 'null'}-${orderRowId ?? 'null'}`;
  }

  private getMultiValueApiParams(rowData: TreeListRowData): {
    subscriptionOrderId: number | null;
    orderRowId: number | null;
  } {
    return rowData.orderItemRowType === OrderItemRowTypeEnumResponseDto.SubscriptionGroup
      ? { subscriptionOrderId: rowData.subscriptionOrderId, orderRowId: null }
      : { subscriptionOrderId: null, orderRowId: rowData.id };
  }

  private setExpandedKey(key: number, isExpanded: boolean): void {
    const next = new Set<number>(this.state.expandedKeys());
    isExpanded ? next.add(key) : next.delete(key);
    this.state.expandedKeys.set(next);
  }

  private clearExpandedKeys(): void {
    this.state.expandedKeys.set(new Set<number>());
  }

  private resetTreeExpandedState(treeInstance?: dxTreeList): void {
    if (!treeInstance) return;
    treeInstance.option('expandedRowKeys', []);
  }

  private hasActiveSelection(): boolean {
    return this.selectionService.selectedRows().length > 0;
  }

  refreshTreeList(options?: { preserveSelection?: boolean; treeInstance?: dxTreeList }): void {
    this.reloadTreeData(options);
  }

  private reloadTreePreservingSelection(treeInstance?: dxTreeList): void {
    const hasActiveSelection = this.hasActiveSelection();
    if (hasActiveSelection) {
      this.selectionService.setIsRestoringSelection(true);
    }
    this.reloadTreeData({ preserveSelection: hasActiveSelection, treeInstance });
  }

  private reloadTreeData(options?: { preserveSelection?: boolean; treeInstance?: dxTreeList }): void {
    const tree = options?.treeInstance ?? this.getTreeInstance();
    if (!tree) return;

    const preserveSelection = options?.preserveSelection ?? false;

    this.resetTreeExpandedState(tree);
    this.clearExpandedKeys();
    if (preserveSelection) {
      this.reloadTreeWithSelectionPreserved(tree);
    } else {
      this.deselectAllRows(tree);
      this.state.pendingSelectionRestore.set(null);
      tree.refresh();
    }
  }

  private reloadTreeWithSelectionPreserved(tree: dxTreeList): void {
    const selectedByParent = this.selectionService.groupSelectedRowsByParent();
    this.state.pendingSelectionRestore.set(this.selectionService.selectedRowsIds());

    tree
      .getDataSource()
      .reload()
      .then(() => {
        const currentData = this.datasourceService.currentData();
        const currentParents = currentData.filter((r) => r.hasItems).map((r) => r.id);
        if (!currentParents || !currentParents.length) return;

        Object.entries(selectedByParent).forEach(([parentId, children]) => {
          const id = Number(parentId);
          if (!currentParents.includes(id)) return;

          const parentRow = currentData.find((r) => r.id === id);
          const selectedCount = children.length;
          const totalCount = parentRow?.orderItemCount ?? 0;

          if (selectedCount < totalCount) {
            this.toggleRow(id, tree);
          }
        });
      });
  }

  get totalCount(): number {
    return this.datasourceService.totalCount();
  }

  get isItemsCheckboxSelected(): boolean {
    return this.selectionService.isItemsCheckboxSelected;
  }

  destroy(): void {
    this.statusService.productPrintableCache.clear();
  }

  private getTreeInstance(): dxTreeList | undefined {
    return this.treeInstanceGetter?.();
  }
}
